import { useEffect, useState, useRef, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, MAT_TYPE, ALL_MACHINES } from "@/lib/ui";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, CalendarRange, CheckCircle2, AlertCircle, Loader2, X, ChevronDown, ChevronRight } from "lucide-react";

export default function ProductionPlan() {
  const { isEditor } = useAuth();
  const [orders, setOrders] = useState([]);
  const [horizon, setHorizon] = useState([]);
  const [form, setForm] = useState({ order_id: "", order_code: "", order_name: "", machine: "", quantity: "", week: "" });
  const [recipe, setRecipe] = useState(null);
  const [recipeErr, setRecipeErr] = useState("");
  const [lookup, setLookup] = useState(false);
  const [mats, setMats] = useState([]);
  const [sel, setSel] = useState({});
  const [editing, setEditing] = useState(null);
  const [efForm, setEfForm] = useState({ quantity: "", week: "" });
  const [of, setOf] = useState({ q: "", machine: "" });
  const [elines, setElines] = useState([]);
  const [collapsed, setCollapsed] = useState({});
  const debounce = useRef();

  const load = () => {
    api.get("/production-orders").then((r) => setOrders(r.data));
    api.get("/materials").then((r) => setMats(r.data));
    api.get("/projection?weeks=12").then((r) => {
      setHorizon(r.data.horizon);
      setForm((f) => ({ ...f, week: f.week || r.data.horizon[0]?.label }));
    });
  };
  useEffect(load, []);

  const GROUP_LABEL = { kraft: "Kraft", lamet: "Lamet", cring: "C-Ring" };
  const neededGroups = recipe
    ? [...new Set(recipe.lines.filter((l) => ["kraft", "lamet", "cring"].includes(l.kind)).map((l) => l.kind))]
    : [];
  const groupMats = (kind) => mats.filter((m) => m.type === kind);

  // tela seçimi: reçetedeki her tela satırı için pozisyonel (1./2./3.) alan
  const telaLines = recipe ? recipe.lines.filter((l) => l.kind === "tela").map((l, i) => ({ ...l, ord: i })) : [];
  const telaOpts = () => mats.filter((m) => m.type === "tela");
  const telaKey = (i) => `tela_${i}`;
  const telaLabel = (ln) => (telaLines.length > 1 ? `${ln.ord + 1}. Tela seçimi` : "Tela seçimi");

  // Mesai (vardiya) = toplam yay adedi (total_coil × adet) / 187200
  const SHIFT_COILS = 187200;
  const mesai = recipe?.total_coil
    ? (recipe.total_coil * (parseFloat(form.quantity) || 0)) / SHIFT_COILS
    : null;

  // tel seçimi: reçetedeki her tel satırı (1., 2. tel) için ayrı alan
  const telGroups = recipe ? recipe.lines.filter((l) => l.kind === "tel").map((l, i) => ({ ...l, ord: i })) : [];
  const telSelMats = mats.filter((m) => m.type === "tel");
  const telSelKey = (i) => `tel_${i}`;

  useEffect(() => {
    if (!recipe) { setSel({}); return; }
    const init = {};
    neededGroups.forEach((g) => {
      const opts = groupMats(g);
      if (g === "lamet" && opts.length) init[g] = opts[0].id;
    });
    telaLines.forEach((l) => {
      const opts = telaOpts(l.color);
      const sameG = opts.filter((o) => o.gramaj === l.gramaj);
      const pool = (sameG.length ? sameG : opts).slice().sort((a, b) => (a.width || 0) - (b.width || 0));
      const match = pool.find((o) => (o.width || 0) >= (l.width || 0) - 1e-9) || pool[pool.length - 1];
      if (match) init[telaKey(l.ord)] = match.id;
    });
    telGroups.forEach((l) => {
      const m = mats.find((x) => x.key === l.key);
      if (m) init[telSelKey(l.ord)] = m.id;
    });
    setSel(init);
    // eslint-disable-next-line
  }, [recipe, mats]);

  const fetchRecipe = (oid) => {
    setRecipe(null);
    setRecipeErr("");
    if (!oid) return;
    setLookup(true);
    api.get(`/recipes/${encodeURIComponent(oid)}`)
      .then((r) => { setRecipe(r.data); setLookup(false); })
      .catch((e) => { setRecipeErr(formatApiError(e.response?.data?.detail)); setLookup(false); });
  };

  const onOrderId = (v) => {
    setForm({ ...form, order_id: v });
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => fetchRecipe(v.trim()), 400);
  };

  const add = async () => {
    if (!form.order_id || !form.quantity || !form.week) { toast.error("Order ID, adet ve hafta gerekli"); return; }
    if (!recipe) { toast.error("Geçerli bir reçete bulunamadı"); return; }
    for (const g of neededGroups) {
      if (!sel[g]) { toast.error(`Lütfen ${GROUP_LABEL[g]} malzemesini seçin`); return; }
    }
    try {
      await api.post("/production-orders", {
        week: form.week, order_id: form.order_id.trim(),
        order_code: form.order_code.trim(), order_name: form.order_name.trim(),
        machine: form.machine, quantity: parseFloat(form.quantity),
        selections: sel,
      });
      toast.success("Sipariş plana eklendi");
      setForm({ order_id: "", order_code: "", order_name: "", machine: form.machine, quantity: "", week: form.week });
      setRecipe(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => { await api.delete(`/production-orders/${id}`); load(); };

  const produce = async (o) => {
    if (o.produced) {
      if (!window.confirm(`Order ${o.order_id} üretildi işareti kaldırılsın ve stok geri eklensin mi?`)) return;
      try { await api.post(`/production-orders/${o.id}/unproduce`); toast.success("Üretim geri alındı, stok iade edildi"); load(); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      return;
    }
    if (!window.confirm(`Order ${o.order_id} üretildi olarak işaretlensin ve stoktan düşülsün mü?`)) return;
    try { await api.post(`/production-orders/${o.id}/produce`); toast.success("Üretildi, stok düşüldü"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEdit = async (o) => {
    setEditing(o); setEfForm({ quantity: o.quantity, week: o.week, order_name: o.order_name || "", machine: o.machine || "" });
    let base = o.custom_lines;
    if (!base) {
      try {
        const { data } = await api.get(`/recipes/${o.order_id}`);
        let telOrd = 0;
        let telaOrd = 0;
        base = data.lines.map((l) => {
          let key = l.key, name = l.name;
          if (["kraft", "lamet", "cring"].includes(l.kind) && o.selections?.[l.kind]) {
            const m = mats.find((x) => x.id === o.selections[l.kind]);
            if (m) { key = m.key; name = m.name; }
          } else if (l.kind === "tela") {
            const sid = o.selections?.[`tela_${telaOrd}`];
            telaOrd += 1;
            if (sid) { const m = mats.find((x) => x.id === sid); if (m) { key = m.key; name = m.name; } }
          } else if (l.kind === "tel") {
            const sid = o.selections?.[`tel_${telOrd}`];
            telOrd += 1;
            if (sid) { const m = mats.find((x) => x.id === sid); if (m) { key = m.key; name = m.name; } }
          }
          return { kind: l.kind, key, name, unit: l.unit, qty_per_unit: l.qty_per_unit };
        });
      } catch { base = []; }
    }
    setElines(base.map((l) => ({ ...l })));
  };
  const saveEdit = async () => {
    try {
      await api.put(`/production-orders/${editing.id}`, {
        quantity: parseFloat(efForm.quantity), week: efForm.week,
        order_name: efForm.order_name.trim(), machine: efForm.machine,
        custom_lines: elines.map((l) => ({ ...l, qty_per_unit: parseFloat(l.qty_per_unit) || 0 })),
      });
      toast.success("Sipariş güncellendi");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const grouped = useMemo(() => {
    const g = {};
    orders.forEach((o) => { (g[o.week] = g[o.week] || []).push(o); });
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [orders]);

  const weekOptions = useMemo(() => {
    const isoLabel = (d) => {
      const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
      const day = t.getUTCDay() || 7; t.setUTCDate(t.getUTCDate() + 4 - day);
      const ys = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
      const wk = Math.ceil((((t - ys) / 86400000) + 1) / 7);
      return `${t.getUTCFullYear()}-H${String(wk).padStart(2, "0")}`;
    };
    const past = [];
    for (let i = 8; i >= 1; i--) { const d = new Date(); d.setUTCDate(d.getUTCDate() - i * 7); past.push(isoLabel(d)); }
    const fut = horizon.map((h) => h.label);
    return [...new Set([...past, ...fut])];
  }, [horizon]);

  const filtered = useMemo(() => {
    const q = of.q.trim().toLowerCase();
    return grouped
      .map(([week, items]) => [week, items.filter((o) =>
        (!q || String(o.order_id).toLowerCase().includes(q) ||
          (o.order_name || "").toLowerCase().includes(q) ||
          (o.order_code || "").toLowerCase().includes(q)) &&
        (!of.machine || o.machine === of.machine)
      )])
      .filter(([, items]) => items.length > 0);
  }, [grouped, of]);

  return (
    <div className="space-y-6 animate-in-up" data-testid="plan-page">
      <div>
        <h1 className="font-head text-3xl font-bold tracking-tight">Üretim Planı / Siparişler</h1>
        <p className="text-[#94A3B8] text-sm mt-1">Order ID girin, reçete otomatik bulunur; adet ve hafta ekleyin</p>
      </div>

      {isEditor && (
        <div className="bg-[#131A26] border border-[#212D40] rounded-xl p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-end">
            <div>
              <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Order ID</label>
              <input
                data-testid="order-id-input"
                value={form.order_id}
                onChange={(e) => onOrderId(e.target.value)}
                placeholder="örn 100001"
                className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]"
              />
            </div>
            <div>
              <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Order Name</label>
              <input
                data-testid="order-name-input"
                value={form.order_name}
                onChange={(e) => setForm({ ...form, order_name: e.target.value })}
                placeholder="örn RELYON / UK260728-7"
                className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]"
              />
            </div>
            <div>
              <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Order Code</label>
              <input
                data-testid="order-code-input"
                value={form.order_code}
                onChange={(e) => setForm({ ...form, order_code: e.target.value })}
                placeholder="örn 211203-6"
                className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]"
              />
            </div>
            <div>
              <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Adet</label>
              <input
                data-testid="order-qty-input"
                type="number"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]"
              />
            </div>
            <div>
              <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Hafta</label>
              <select
                data-testid="order-week-select"
                value={form.week}
                onChange={(e) => setForm({ ...form, week: e.target.value })}
                className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]"
              >
                {weekOptions.map((l) => <option key={l} value={l}>{l}</option>)}
                <option value="AKTİF OLMAYAN">AKTİF OLMAYAN (ertelendi)</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Makine</label>
              <select
                data-testid="order-machine-select"
                value={form.machine}
                onChange={(e) => setForm({ ...form, machine: e.target.value })}
                className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]"
              >
                <option value="">Seçin...</option>
                {ALL_MACHINES.map((mc) => <option key={mc} value={mc}>{mc}</option>)}
              </select>
            </div>
          </div>
          <button
            data-testid="order-add-button"
            onClick={add}
            className="mt-3 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-sm font-semibold w-full sm:w-auto"
          >
            <Plus className="w-4 h-4" /> Ekle
          </button>

          {/* recipe preview */}
          <div className="mt-4" data-testid="order-recipe-preview">
            {lookup && <div className="flex items-center gap-2 text-sm text-[#94A3B8]"><Loader2 className="w-4 h-4 animate-spin" /> Reçete aranıyor...</div>}
            {recipeErr && (
              <div className="flex items-center gap-2 text-sm text-[#EF4444]">
                <AlertCircle className="w-4 h-4" /> {recipeErr}
              </div>
            )}
            {recipe && (
              <div className="bg-[#0F1520] border border-[#212D40] rounded-lg p-4">
                <div className="flex items-center gap-2 text-sm mb-2">
                  <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                  <span className="font-semibold">Reçete bulundu:</span>
                  <span className="font-num">{recipe.order_id}</span>
                  <span className="text-[#94A3B8]">{recipe.size} · {recipe.wire_raw}</span>
                </div>
                {recipe.total_coil ? (
                  <div className="flex flex-wrap items-center gap-2 mb-2 text-xs" data-testid="order-mesai-window">
                    <span className="bg-[#131A26] border border-[#212D40] rounded px-2.5 py-1">
                      Yay/adet: <span className="font-num">{fmt(recipe.total_coil)}</span>
                    </span>
                    <span className="bg-[#1E293B] border border-[#334155] rounded px-2.5 py-1 text-[#38BDF8]">
                      Mesai: <span className="font-num font-semibold" data-testid="order-mesai-value">{(mesai ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> vardiya
                      {!form.quantity && <span className="text-[#64748B]"> (adet girin)</span>}
                    </span>
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  {recipe.lines.map((l, i) => {
                    const t = MAT_TYPE[l.kind] || {};
                    const q = form.quantity ? parseFloat(form.quantity) : 0;
                    const total = l.qty_per_unit * q;
                    return (
                      <span key={i} className="text-xs bg-[#131A26] border border-[#212D40] rounded px-2.5 py-1">
                        <span style={{ color: t.color }}>●</span> {l.name}:{" "}
                        <span className="font-num">{fmt(l.qty_per_unit)}{q ? ` × ${fmt(q)} = ${fmt(total)}` : ""} {l.unit}</span>
                      </span>
                    );
                  })}
                </div>
                {neededGroups.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[#212D40] grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {neededGroups.map((g) => (
                      <div key={g}>
                        <label className="text-xs text-[#94A3B8] uppercase tracking-wider">{GROUP_LABEL[g]} seçimi *</label>
                        <select
                          data-testid={`order-select-${g}`}
                          value={sel[g] || ""}
                          onChange={(e) => setSel({ ...sel, [g]: e.target.value })}
                          className="w-full mt-1 bg-[#131A26] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]"
                        >
                          <option value="">Seçin...</option>
                          {groupMats(g).map((m) => (
                            <option key={m.id} value={m.id}>{m.name}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                )}
                {telGroups.length > 1 && (
                  <div className="mt-3 pt-3 border-t border-[#212D40] grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {telGroups.map((l) => (
                      <div key={l.ord}>
                        <label className="text-xs text-[#94A3B8] uppercase tracking-wider">{l.ord + 1}. Tel seçimi</label>
                        <select
                          data-testid={`order-select-tel-${l.ord}`}
                          value={sel[telSelKey(l.ord)] || ""}
                          onChange={(e) => setSel({ ...sel, [telSelKey(l.ord)]: e.target.value })}
                          className="w-full mt-1 bg-[#131A26] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]"
                        >
                          <option value="">Otomatik (reçeteye göre)</option>
                          {telSelMats.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                )}
                {telaLines.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[#212D40] grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {telaLines.map((l) => (
                      <div key={l.ord}>
                        <label className="text-xs text-[#94A3B8] uppercase tracking-wider">{telaLabel(l)}</label>
                        <select
                          data-testid={`order-select-tela-${l.ord}`}
                          value={sel[telaKey(l.ord)] || ""}
                          onChange={(e) => setSel({ ...sel, [telaKey(l.ord)]: e.target.value })}
                          className="w-full mt-1 bg-[#131A26] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]"
                        >
                          <option value="">Otomatik (reçeteye göre)</option>
                          {telaOpts(l.color).map((m) => (
                            <option key={m.id} value={m.id}>{m.name}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2" data-testid="order-filter-bar">
        <input data-testid="order-filter-search" placeholder="Order ID, isim veya kod ara..." value={of.q}
          onChange={(e) => setOf({ ...of, q: e.target.value })}
          className="bg-[#131A26] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB] w-64" />
        <select data-testid="order-filter-machine" value={of.machine} onChange={(e) => setOf({ ...of, machine: e.target.value })}
          className="bg-[#131A26] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]">
          <option value="">Tüm Makineler</option>
          {[...new Set(orders.map((o) => o.machine).filter(Boolean))].sort().map((mc) => <option key={mc} value={mc}>{mc}</option>)}
        </select>
        {(of.q || of.machine) && (
          <button data-testid="order-filter-clear" onClick={() => setOf({ q: "", machine: "" })}
            className="text-xs px-3 py-2 rounded-lg bg-[#131A26] border border-[#212D40] text-[#94A3B8] hover:text-white">Temizle</button>
        )}
      </div>

      <div className="space-y-4">
        {grouped.length === 0 && <div className="text-[#64748B] text-sm">Henüz sipariş eklenmedi.</div>}
        {grouped.length > 0 && filtered.length === 0 && <div className="text-[#64748B] text-sm" data-testid="order-filter-empty">Filtreye uygun sipariş bulunamadı.</div>}
        {filtered.map(([week, items]) => (
          <div key={week} className="bg-[#131A26] border border-[#212D40] rounded-xl overflow-hidden">
            <div onClick={() => setCollapsed((c) => ({ ...c, [week]: !c[week] }))} data-testid={`week-header-${week}`}
                 className="flex items-center gap-2 px-5 py-3 border-b border-[#212D40] bg-[#0F1520] cursor-pointer select-none">
              {collapsed[week] ? <ChevronRight className="w-4 h-4 text-[#94A3B8]" /> : <ChevronDown className="w-4 h-4 text-[#94A3B8]" />}
              <CalendarRange className="w-4 h-4 text-[#2563EB]" />
              <span className="font-head text-lg font-semibold">{week}</span>
              <span className="text-xs text-[#64748B]">({items.length} sipariş)</span>
            </div>
            {!collapsed[week] && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-[#94A3B8] uppercase tracking-wider">
                  <th className="py-2 px-5">Order ID</th>
                  <th className="py-2 px-5">Order Name</th>
                  <th className="py-2 px-5">Makine</th>
                  <th className="py-2 px-5">Order Code</th>
                  <th className="py-2 px-5">Ölçü</th>
                  <th className="py-2 px-5 text-right">Adet</th>
                  <th className="py-2 px-5"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((o) => (
                  <tr key={o.id} className="border-t border-[#1a2334] hover:bg-[#0F1520]" data-testid={`order-row-${o.id}`}>
                    <td className="py-2.5 px-5 font-num font-semibold whitespace-nowrap" data-testid={`order-cell-id-${o.id}`}>
                      <span className="inline-block text-[10px] font-semibold text-[#38BDF8] bg-[rgba(56,189,248,0.12)] border border-[rgba(56,189,248,0.3)] rounded px-1.5 py-0.5 mr-2 align-middle">{(() => { const m2 = String(o.week || "").match(/H(\d+)/); return m2 ? `HF${m2[1]}` : (o.week || "—"); })()}</span>{o.order_id}
                    </td>
                    <td className="py-2.5 px-5">{o.order_name || "—"}</td>
                    <td className="py-2.5 px-5 text-[#94A3B8]">{o.machine || "—"}</td>
                    <td className="py-2.5 px-5 font-num text-[#94A3B8]">{o.order_code || "—"}</td>
                    <td className="py-2.5 px-5 text-[#94A3B8]">{o.size || "—"}</td>
                    <td className="py-2.5 px-5 text-right font-num">{fmt(o.quantity)}</td>
                    <td className="py-2.5 px-5 text-right w-24">
                      {isEditor && (
                        <>
                          <button onClick={() => produce(o)} data-testid={`order-produce-${o.id}`} title={o.produced ? "Üretildi (kaldırmak için tıklayın)" : "Üretildi olarak işaretle"}
                            className={`mr-3 ${o.produced ? "text-[#10B981]" : "text-[#64748B] hover:text-[#10B981]"}`}>
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => openEdit(o)} data-testid={`order-edit-${o.id}`} className="text-[#64748B] hover:text-[#2563EB] mr-3">
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => remove(o.id)} data-testid={`order-delete-${o.id}`} className="text-[#64748B] hover:text-[#EF4444]">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-[#131A26] border border-[#212D40] rounded-xl w-full max-w-lg max-h-[88vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-[#212D40]">
              <h3 className="font-head text-xl font-semibold">Sipariş Düzenle · {editing.order_id}</h3>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5 text-[#94A3B8]" /></button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto">
              <div>
                <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Order Name</label>
                <input data-testid="order-edit-name" value={efForm.order_name}
                  onChange={(e) => setEfForm({ ...efForm, order_name: e.target.value })}
                  placeholder="örn RELYON / UK260728-7"
                  className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Adet</label>
                  <input data-testid="order-edit-qty" type="number" value={efForm.quantity}
                    onChange={(e) => setEfForm({ ...efForm, quantity: e.target.value })}
                    className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
                </div>
                <div>
                  <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Hafta</label>
                  <select data-testid="order-edit-week" value={efForm.week}
                    onChange={(e) => setEfForm({ ...efForm, week: e.target.value })}
                    className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]">
                    {efForm.week && efForm.week !== "AKTİF OLMAYAN" && !weekOptions.includes(efForm.week) && (
                      <option value={efForm.week}>{efForm.week}</option>
                    )}
                    {weekOptions.map((l) => <option key={l} value={l}>{l}</option>)}
                    <option value="AKTİF OLMAYAN">AKTİF OLMAYAN (ertelendi)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Makine</label>
                <select data-testid="order-edit-machine" value={efForm.machine}
                  onChange={(e) => setEfForm({ ...efForm, machine: e.target.value })}
                  className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]">
                  <option value="">Seçin...</option>
                  {ALL_MACHINES.map((mc) => <option key={mc} value={mc}>{mc}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Tüketimler (bu siparişe özel)</label>
                <div className="space-y-2 mt-2" data-testid="order-edit-lines">
                  {elines.map((l, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      {(l.kind === "tel" || l.kind === "tela") ? (
                        <select value={l.key}
                          onChange={(e) => {
                            const m = mats.find((x) => x.key === e.target.value);
                            const arr = [...elines]; arr[i] = { ...l, key: m.key, name: m.name }; setElines(arr);
                          }}
                          className="flex-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-2 py-1.5 text-sm outline-none focus:border-[#2563EB]">
                          {mats.filter((m) => m.type === l.kind).map((m) => (
                            <option key={m.key} value={m.key}>{m.name}</option>
                          ))}
                        </select>
                      ) : (
                        <span className="flex-1 text-sm truncate">{l.name}</span>
                      )}
                      <input type="number" value={l.qty_per_unit}
                        onChange={(e) => { const arr = [...elines]; arr[i] = { ...l, qty_per_unit: e.target.value }; setElines(arr); }}
                        className="w-24 bg-[#0F1520] border border-[#212D40] rounded-lg px-2 py-1.5 text-sm text-right font-num outline-none focus:border-[#2563EB]" />
                      <span className="text-xs text-[#64748B] w-8">{l.unit}</span>
                    </div>
                  ))}
                  {elines.length === 0 && <div className="text-xs text-[#64748B]">Reçete satırı yok.</div>}
                </div>
                <p className="text-[10px] text-[#64748B] mt-1">Not: adet başına değerlerdir; tel için ayrıca +%1 fire eklenir.</p>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-[#212D40]">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg text-sm text-[#94A3B8] hover:bg-[#0F1520]">İptal</button>
              <button data-testid="order-edit-save" onClick={saveEdit} className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#2563EB] hover:bg-[#1D4ED8]">Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
